// @flow
import {
    deleteDashboard,
    getDashboards,
    getReports,
    deleteReport,
    getGrids,
    getVersionFlags,
    deleteGrid,
    addGrid,
    postNewFilter,
    deleteFilter,
    patchFilter,
    shareAttachment,
    getUsers,
    updateDashboardUsers,
    patchDashboard,
} from './client';

export function logout(): Action {
    return { type: 'LOGOUT' };
}

export function login(user: User): Action {
    return { type: 'LOGIN', user: user };
}

export function updatePermissionsGetAndStoreUsers(
    clientId: Guid,
    dashboardId: Guid,
    permissions: Array<{ id: Guid, selected: boolean }>
): ThunkAction {
    return function (dispatch) {
        return updateDashboardUsers(clientId, dashboardId, permissions)
            .then(() => getUsers(clientId).then((users) => dispatch(setUsers(users))))
            .then(() =>
                dispatch(
                    addNotification({
                        message: 'Permissions Saved!',
                        key: undefined,
                        style: undefined,
                    })
                )
            )
            .catch(() =>
                dispatch(
                    addNotification({
                        message: 'An error occurred saving the permissions. Try again.',
                        style: { color: '#fff', backgroundColor: '#d00' },
                        key: undefined,
                    })
                )
            );
    };
}

export function getAndStoreUsers(clientId: Guid): ThunkAction {
    return function (dispatch, getState) {
        const state = getState();
        const isAdmin = state.user.isAdmin && state.user.isAdmin.indexOf(clientId) !== -1;
        const isManager = state.user.isManager && state.user.isManager.indexOf(clientId) !== -1;
        if (isAdmin || isManager) {
            return getUsers(clientId).then((users) => dispatch(setUsers(users)));
        } else {
            return Promise.resolve();
        }
    };
}

function setUsers(users: UserPermissions[]): Action {
    return { type: 'SET_USERS', users: users };
}

function setDashboards(dashboards): Action {
    return { type: 'SET_DASHBOARDS', dashboards: dashboards };
}

function setDashboardsError(message: string): Action {
    return { type: 'SET_DASHBOARDS_ERROR', message: message };
}

function Share(analyticsId: Guid, analyticsType: string, messageDetails: any) {
    return function (dispatch) {
        return shareAttachment(`${analyticsType}/${analyticsId}`, messageDetails)
            .then(() => dispatch(addNotification({ message: 'Message Sent!', style: undefined })))
            .catch(() =>
                dispatch(
                    addNotification({
                        message: 'An error occurred sending the message. Try again.',
                        style: { color: '#fff', backgroundColor: '#d00' },
                    })
                )
            );
    };
}

export function shareReport(reportId: Guid, messageDetails: any): ThunkAction {
    return Share(reportId, 'report', messageDetails);
}

export function shareDashboard(dashboardId: Guid, messageDetails: any): ThunkAction {
    return Share(dashboardId, 'dashboard', messageDetails);
}

export function getAndStoreDashboards(clientId: Guid): ThunkAction {
    return function (dispatch) {
        return getDashboards(clientId)
            .then((dashboards) => {
                dispatch(setDashboards(dashboards));
            })
            .catch((err) => {
                // Clear the list so the previous company's dashboards (and their links) aren't left
                // on screen. Show the server's message for 4xx and 503, which are written for the user.
                const useServerMessage = err.status < 500 || err.status === 503;
                dispatch(
                    setDashboardsError(
                        useServerMessage && err.message
                            ? err.message
                            : 'Dashboards could not be loaded for this company. Contact SBI support if this continues.'
                    )
                );
            });
    };
}

export function deleteGetAndStoreDashboards(dashboardId: Guid, clientId: Guid): ThunkAction {
    return function (dispatch) {
        return deleteDashboard(dashboardId, clientId)
            .then(() => getDashboards(clientId))
            .then((dashboards) => {
                dispatch(setDashboards(dashboards));
            })
            .then(() =>
                dispatch(
                    addNotification({
                        message: 'Dashboard Deleted!',
                        style: undefined,
                        key: undefined,
                    })
                )
            )
            .catch(() =>
                dispatch(
                    addNotification({
                        message: 'An error occurred deleting the dashboard. Try again.',
                        style: { color: '#fff', backgroundColor: '#d00' },
                        key: undefined,
                    })
                )
            );
    };
}

export function patchGetAndStoreDashboards(
    dashboardId: Guid,
    clientId: Guid,
    options: { description: string, statusId: number }
): ThunkAction {
    return function (dispatch) {
        return patchDashboard(dashboardId, clientId, options)
            .then(() => getDashboards(clientId))
            .then((dashboards) => {
                dispatch(setDashboards(dashboards));
            })
            .then(() =>
                dispatch(
                    addNotification({
                        message: 'Dashboard Updated!',
                        style: undefined,
                        key: undefined,
                    })
                )
            )
            .catch(() =>
                dispatch(
                    addNotification({
                        message: 'An error occurred updating the dashboard. Try again.',
                        style: { color: '#fff', backgroundColor: '#d00' },
                        key: undefined,
                    })
                )
            );
    };
}

function setReports(reports): Action {
    return { type: 'SET_REPORTS', reports: reports };
}

export function getAndStoreReports(clientId: Guid): ThunkAction {
    return function (dispatch) {
        return getReports(clientId).then((reports) => {
            dispatch(setReports(reports));
        });
    };
}

export function deleteGetAndStoreReports(reportId: Guid, clientId: Guid): ThunkAction {
    return function (dispatch) {
        return deleteReport(reportId, clientId)
            .then(() => getReports(clientId))
            .then((reports) => {
                dispatch(setReports(reports));
            })
            .then(() =>
                dispatch(
                    addNotification({
                        message: 'Report Deleted!',
                        style: undefined,
                        key: undefined,
                    })
                )
            )
            .catch(() =>
                dispatch(
                    addNotification({
                        message: 'An error occurred deleting the report. Try again.',
                        style: { color: '#fff', backgroundColor: '#d00' },
                        key: undefined,
                    })
                )
            );
    };
}

function setGrids(grids: any[]): Action {
    return { type: 'SET_GRIDS', grids: grids };
}

export function getAndStoreGrids(clientId: Guid): ThunkAction {
    return function (dispatch) {
        return getGrids(clientId).then((grids) => {
            dispatch(setGrids(grids));
        });
    };
}

export function deleteGetAndStoreGrids(gridId: Guid, clientId: Guid): ThunkAction {
    return function (dispatch) {
        return deleteGrid(gridId, clientId)
            .then(() => getGrids(clientId))
            .then((grids) => {
                dispatch(setGrids(grids));
            })
            .then(() =>
                dispatch(
                    addNotification({ message: 'Grid Deleted!', style: undefined, key: undefined })
                )
            )
            .catch(() =>
                dispatch(
                    addNotification({
                        message: 'An error occurred deleting the grid.  Try again.',
                        style: { color: '#fff', backgroundColor: '#d00' },
                        key: undefined,
                    })
                )
            );
    };
}

export function addGetAndStoreGrids(clientId: Guid, name: string, sprocId: Guid): ThunkAction {
    return function (dispatch) {
        return addGrid(clientId, name, sprocId).then((result) => {
            return getGrids(clientId).then((grids) => {
                dispatch(setGrids(grids));
                return result.grids[0].uID;
            });
        });
    };
}

export function addFilterAndGetAndStoreGrids(
    gridId: Guid,
    filters: { [string]: string[] },
    name: string,
    clientId: Guid
): ThunkAction {
    return function (dispatch) {
        return postNewFilter(gridId, filters, name, clientId).then((result) => {
            return getGrids(clientId).then((grids) => {
                dispatch(setGrids(grids));
                return result.filters[0].uID;
            });
        });
    };
}

export function editFilterAndGetAndStoreGrids(
    filterId: Guid,
    filterDefinition: { [string]: string[] },
    name: string,
    clientId: Guid
): ThunkAction {
    return function (dispatch) {
        return patchFilter(filterId, filterDefinition, name, clientId).then((result) => {
            return getGrids(clientId).then((grids) => {
                dispatch(setGrids(grids));
            });
        });
    };
}

export function deleteFilterAndGetAndStoreGrids(filterId: Guid, clientId: Guid): ThunkAction {
    return function (dispatch) {
        return deleteFilter(filterId, clientId).then((result) => {
            return getGrids(clientId)
                .then((grids) => dispatch(setGrids(grids)))
                .then(() =>
                    dispatch(
                        addNotification({
                            message: 'Filter Deleted!',
                            style: undefined,
                            key: undefined,
                        })
                    )
                )
                .catch(() =>
                    dispatch(
                        addNotification({
                            message: 'An error occurred deleting the filter.  Try again.',
                            style: { color: '#fff', backgroudnColor: '#d00' },
                            key: undefined,
                        })
                    )
                );
        });
    };
}

export function removeNotification(key: number) {
    return { type: 'NOTIFICATIONS/REMOVE', key: key };
}

function addNotification(notification: SbiNotification) {
    return { type: 'NOTIFICATIONS/ADD', notification: notification };
}

function setVersionFlags(versionFlags) {
    return { type: 'SET_VERSION_FLAGS', versionFlags: versionFlags };
}

export function getAndStoreVersionFlags(clientId: Guid): ThunkAction {
    return function (dispatch) {
        return getVersionFlags(clientId).then((versionFlags) => {
            dispatch(setVersionFlags(versionFlags));
        });
    };
}



// WEBPACK FOOTER //
// ./modules/actions.js